// Cheatsheet content per card / alert type.
//
// Every command is written with `<TOKEN>` placeholders for any value that
// can come from the clicked card's context. `fillCommand` substitutes
// them at render time.
//
// Rules:
//   * Commands marked `danger: true` are destructive (state changes, GPU
//     resets, kernel module reload). They render with a warning stripe.
//   * Keep it tight — the user asked explicitly for fewer, accurate
//     commands over long lists of half-remembered flags.

import type { Section, CheatsheetCtx } from "./types";

// ---------------------------------------------------------------------------
// Per-card content
// ---------------------------------------------------------------------------

const GPUS_BUSY: Section[] = [
  {
    label: "Inspect",
    cmds: [
      { cmd: "nvidia-smi" },
      { cmd: "nvidia-smi --query-gpu=index,name,utilization.gpu,memory.used --format=csv" },
      { cmd: "dcgmi dmon -d 1" },
    ],
  },
  {
    label: "Slurm allocation",
    cmds: [
      { cmd: 'sinfo -O "NodeList,Gres,GresUsed"' },
      { cmd: "squeue --Format=JobID,UserName,Partition,NodeList,tres-alloc" },
    ],
  },
  {
    label: "Live monitoring",
    cmds: [{ cmd: "nvidia-smi dmon -s pucvmet -d 1" }],
  },
];

const POWER_DRAW: Section[] = [
  {
    label: "Inspect",
    cmds: [
      { cmd: "nvidia-smi --query-gpu=power.draw,power.limit,power.default_limit --format=csv" },
      { cmd: "dcgmi dmon -e 155,156,157", note: "FIELD_ID 155=power usage, 156=total energy, 157=SM clock" },
    ],
  },
  {
    label: "Node-level",
    cmds: [
      { cmd: "ipmitool -H <BMC_IP> sensor | grep -i power" },
      { cmd: "ipmitool -H <BMC_IP> dcmi power reading" },
    ],
  },
  {
    label: "Limits",
    cmds: [
      { cmd: "nvidia-smi -pl <WATTS>", note: "requires sudo", danger: true },
    ],
  },
];

const QUEUE: Section[] = [
  {
    label: "Inspect",
    cmds: [
      { cmd: "squeue" },
      { cmd: "squeue --me" },
      { cmd: "squeue --start", note: "estimated start times" },
      { cmd: 'squeue --format="%.10i %.9P %.20j %.8u %.2t %.10M %.6D %R"' },
    ],
  },
  {
    label: "Priority investigation",
    cmds: [
      { cmd: "sprio -l -u <USER>" },
      { cmd: "sprio -n", note: "normalized priorities" },
    ],
  },
  {
    label: "Job details",
    cmds: [
      { cmd: "scontrol show job <JOB_ID>" },
      { cmd: "sacct -j <JOB_ID> --format=JobID,State,ExitCode,Reason,ReqTRES,AllocTRES" },
      { cmd: "seff <JOB_ID>", note: "efficiency report" },
    ],
  },
  {
    label: "Actions",
    cmds: [
      { cmd: "scancel <JOB_ID>", danger: true },
      { cmd: "scontrol hold <JOB_ID>" },
      { cmd: "scontrol release <JOB_ID>" },
    ],
  },
];

const FABRIC: Section[] = [
  {
    label: "Quick status",
    cmds: [
      { cmd: "ibstat" },
      { cmd: "ibstatus" },
      { cmd: 'ibv_devinfo | grep -E "hca_id|state|rate"' },
    ],
  },
  {
    label: "Topology",
    cmds: [
      { cmd: "ibnetdiscover -p", note: "list ports" },
      { cmd: "iblinkinfo" },
    ],
  },
  {
    label: "Counters",
    cmds: [
      { cmd: "perfquery", note: "local port" },
      { cmd: "perfquery -a", note: "all ports on fabric" },
      { cmd: "ibqueryerrors --clear-errors=all", note: "show + reset", danger: true },
    ],
  },
  {
    label: "Diagnostics",
    cmds: [
      { cmd: "ibdiagnet -P all=1", note: "full fabric diag" },
      { cmd: "smpquery NI <LID>", note: "subnet manager query" },
    ],
  },
];

const TOPOLOGY_NODE: Section[] = [
  {
    label: "Inspect",
    cmds: [
      { cmd: "scontrol show node <NODE>" },
      { cmd: "sinfo -N -l | grep <NODE>" },
    ],
  },
  {
    label: "Node-level drill",
    cmds: [
      { cmd: 'ssh <NODE> "uptime && free -h && nproc"' },
      { cmd: 'ssh <NODE> "nvidia-smi"' },
      { cmd: 'ssh <NODE> "ibstat"' },
      { cmd: 'ssh <NODE> "dmesg -T | tail -50"' },
    ],
  },
  {
    label: "State changes (careful!)",
    cmds: [
      { cmd: 'scontrol update NodeName=<NODE> State=drain Reason="maintenance"', danger: true },
      { cmd: "scontrol update NodeName=<NODE> State=resume", danger: true },
      { cmd: 'scontrol update NodeName=<NODE> State=down Reason="hw issue"', danger: true },
    ],
  },
];

const PARTITION: Section[] = [
  {
    label: "Inspect",
    cmds: [
      { cmd: "sinfo -s", note: "summary" },
      { cmd: "sinfo -p <PARTITION>" },
      { cmd: "scontrol show partition <PARTITION>" },
    ],
  },
  {
    label: "What's running / waiting",
    cmds: [
      { cmd: "squeue -p <PARTITION>" },
      { cmd: "squeue -p <PARTITION> --start", note: "waiting + ETA" },
    ],
  },
  {
    label: "History",
    cmds: [
      { cmd: "sreport cluster utilization start=now-7days" },
      { cmd: "sacct -X -S now-1day -p <PARTITION> --format=JobID,Elapsed,State" },
    ],
  },
];

const GPU_FLEET: Section[] = [
  {
    label: "Live",
    cmds: [
      { cmd: "nvidia-smi dmon -d 1" },
      { cmd: "dcgmi dmon -d 1000" },
    ],
  },
  {
    label: "Health",
    cmds: [
      { cmd: "nvidia-smi -q -d ECC" },
      { cmd: "nvidia-smi -q -d TEMPERATURE,POWER,CLOCK" },
      { cmd: "dcgmi diag -r 2", note: "short diagnostic run" },
      { cmd: "dcgmi diag -r 3", note: "full diagnostic run (heavy)", danger: true },
    ],
  },
  {
    label: "NVLink",
    cmds: [
      { cmd: "nvidia-smi nvlink --status" },
      { cmd: "nvidia-smi nvlink -e", note: "errors" },
    ],
  },
];

const FABRIC_HEALTH: Section[] = [
  {
    label: "Symbol errors",
    cmds: [
      { cmd: 'perfquery -a | grep -iE "symbolerror|linkerror|recoveries"' },
      { cmd: "ibqueryerrors" },
    ],
  },
  {
    label: "Link check",
    cmds: [
      { cmd: "iblinkinfo | grep -v Active", note: "non-active links" },
      { cmd: "ibdiagnet -P all=1" },
    ],
  },
  {
    label: "HCA check",
    cmds: [
      { cmd: "ibstat" },
      { cmd: "rdma link show" },
    ],
  },
];

const ERRORS_TREND: Section[] = [
  {
    label: "GPU errors",
    cmds: [
      { cmd: 'ssh <NODE> "nvidia-smi -q -d ERRORS,ECC,PAGE_RETIREMENT"' },
      { cmd: 'ssh <NODE> "dmesg -T | grep -iE \'xid|nvrm|nvidia\'"' },
    ],
  },
  {
    label: "IB symbol-error counters",
    cmds: [
      { cmd: "perfquery -a | grep -i symbol" },
      { cmd: "ibqueryerrors --data" },
      { cmd: "ibqueryerrors --clear-errors=all", note: "reset counters after root cause", danger: true },
    ],
  },
];

const GPU_CARD: Section[] = [
  {
    label: "Inspect this GPU",
    cmds: [
      { cmd: "nvidia-smi -i <GPU_IDX>" },
      { cmd: 'ssh <NODE> "nvidia-smi -q -i <GPU_IDX> -d ECC,POWER,TEMPERATURE,CLOCK,UTILIZATION"' },
      { cmd: 'ssh <NODE> "dcgmi dmon -i <GPU_IDX> -d 1"' },
    ],
  },
  {
    label: "NVLink / PCIe",
    cmds: [
      { cmd: 'ssh <NODE> "nvidia-smi nvlink --status -i <GPU_IDX>"' },
      { cmd: 'ssh <NODE> "lspci -vvv -s $(nvidia-smi -i <GPU_IDX> --query-gpu=pci.bus_id --format=csv,noheader)"' },
    ],
  },
  {
    label: "Reset (requires idle GPU)",
    cmds: [
      { cmd: 'ssh <NODE> "sudo nvidia-smi --gpu-reset -i <GPU_IDX>"', danger: true },
    ],
  },
];

const IB_PORT: Section[] = [
  {
    label: "Port counters",
    cmds: [
      { cmd: 'ssh <NODE> "ibstat <HCA> <PORT>"' },
      { cmd: 'ssh <NODE> "perfquery -x <LID> <PORT>"', note: "extended counters; find LID with ibstat" },
      { cmd: 'ssh <NODE> "cat /sys/class/infiniband/<HCA>/ports/<PORT>/counters/symbol_error"' },
    ],
  },
  {
    label: "Physical layer",
    cmds: [
      { cmd: 'ssh <NODE> "cat /sys/class/infiniband/<HCA>/ports/<PORT>/rate"' },
      { cmd: 'ssh <NODE> "mlxcables"', note: "SFP/QSFP health; requires mlnx-ofed" },
    ],
  },
];

const CARD_CONTENT: Record<string, Section[]> = {
  gpus_busy: GPUS_BUSY,
  power_draw: POWER_DRAW,
  queue: QUEUE,
  fabric: FABRIC,
  topology_node: TOPOLOGY_NODE,
  partition: PARTITION,
  gpu_fleet: GPU_FLEET,
  fabric_health: FABRIC_HEALTH,
  errors_trend: ERRORS_TREND,
  gpu_card: GPU_CARD,
  ib_port: IB_PORT,
};

// ---------------------------------------------------------------------------
// Per-alert content
// ---------------------------------------------------------------------------

const ALERT_CONTENT: Record<string, Section[]> = {
  XIDErrorDetected: [
    {
      label: "Investigate on the node",
      cmds: [
        { cmd: 'ssh <NODE> "dmesg -T | grep -i nvrm"' },
        { cmd: 'ssh <NODE> "journalctl -k -S \'1 hour ago\' | grep -i xid"' },
        { cmd: 'ssh <NODE> "nvidia-smi -q -d ERRORS,ECC,PAGE_RETIREMENT -i <GPU_IDX>"' },
      ],
    },
    {
      label: "XID code reference",
      cmds: [
        { cmd: "# XID 31 = GPU MMU fault (often driver/app bug, sometimes HW)" },
        { cmd: "# XID 43 = invalid SM instruction" },
        { cmd: "# XID 48/63/64 = ECC errors" },
        { cmd: "# XID 79 = GPU fallen off the bus (HARDWARE, usually fatal)" },
        { cmd: "# XID 119/120 = GSP timeout" },
      ],
    },
    {
      label: "Decision flow",
      cmds: [
        { cmd: "# XID 79 → drain node, RMA candidate" },
        { cmd: "# XID 31/43 → usually software, restart app; if repeats on diff apps → HW suspect" },
        { cmd: "# XID 48/63/64 → check ECC counters, maybe retire page" },
      ],
    },
    {
      label: "Actions",
      cmds: [
        { cmd: 'scontrol update NodeName=<NODE> State=drain Reason="XID <CODE>"', danger: true },
        { cmd: 'ssh <NODE> "sudo nvidia-smi --gpu-reset -i <GPU_IDX>"', note: "requires no running workload", danger: true },
      ],
    },
  ],

  ECCSBEWarn: [
    {
      label: "Inspect",
      cmds: [
        { cmd: 'ssh <NODE> "nvidia-smi -q -d ECC -i <GPU_IDX>"' },
        { cmd: 'ssh <NODE> "nvidia-smi -q -d PAGE_RETIREMENT -i <GPU_IDX>"' },
      ],
    },
    {
      label: "Key metrics to check",
      cmds: [
        { cmd: "# Volatile SBE/DBE counts since last reset" },
        { cmd: "# Aggregate SBE/DBE counts lifetime" },
        { cmd: "# Retired pages: single/double bit" },
        { cmd: "# Pending page blacklist" },
      ],
    },
    {
      label: "Decision",
      cmds: [
        { cmd: "# SBE rate < threshold → monitor, no action" },
        { cmd: "# SBE rate rising → drain after current job, RMA evaluation" },
        { cmd: "# Any DBE → drain immediately" },
      ],
    },
    {
      label: "Actions",
      cmds: [
        { cmd: 'scontrol update NodeName=<NODE> State=drain Reason="ECC SBE elevated"', danger: true },
      ],
    },
  ],

  SlurmPartitionFullyAllocated: [
    {
      label: "Inspect",
      cmds: [
        { cmd: "sinfo -p <PARTITION>" },
        { cmd: "scontrol show partition <PARTITION>" },
        { cmd: 'squeue -p <PARTITION> -t R --format="%.10i %.20j %.8u %.10M %.10l"' },
      ],
    },
    {
      label: "Who's waiting",
      cmds: [
        { cmd: 'squeue -p <PARTITION> -t PD --format="%.10i %.20j %.8u %.8r %.10L"' },
      ],
    },
    {
      label: "Priority / fairshare",
      cmds: [
        { cmd: "sprio -p <PARTITION>" },
        { cmd: 'sshare -a | grep -E "Account|User"' },
      ],
    },
    {
      label: "Pattern check",
      cmds: [
        { cmd: "sacct -X -S now-24hours -r <PARTITION> --format=JobID,Elapsed,State | sort | uniq" },
      ],
    },
  ],

  LongQueueWait: [
    {
      label: "Why pending?",
      cmds: [
        { cmd: "scontrol show job <JOB_ID>" },
        { cmd: 'squeue -j <JOB_ID> --format="%.10i %.8u %.2t %.8r %R"' },
        { cmd: "# Reason codes: Resources, Priority, Dependency, QOSMaxJobsPerUser, ReqNodeNotAvail" },
      ],
    },
    {
      label: "Starvation or fair?",
      cmds: [
        { cmd: "sprio -j <JOB_ID>" },
        { cmd: "squeue -p <PARTITION> -t R --sort=-M", note: "oldest running first" },
      ],
    },
    {
      label: "ETA",
      cmds: [{ cmd: "squeue -j <JOB_ID> --start" }],
    },
    {
      label: "Options",
      cmds: [
        { cmd: "scontrol update JobID=<JOB_ID> NumNodes=<LOWER>", note: "ask less → schedule sooner", danger: true },
        { cmd: "scontrol update JobID=<JOB_ID> Partition=<BACKUP_PARTITION>", danger: true },
      ],
    },
  ],

  IBLinkDown: [
    {
      label: "Locate",
      cmds: [
        { cmd: "iblinkinfo | grep -v Active" },
        { cmd: "ibstat" },
        { cmd: "ibnetdiscover -p | grep <LID>" },
      ],
    },
    {
      label: "Investigate",
      cmds: [
        { cmd: "perfquery -x <LID> <PORT>", note: "extended counters" },
        { cmd: 'ssh <NODE> "ibstat -p"' },
        { cmd: 'ssh <NODE> "dmesg -T | grep -iE \'mlx|ib_|infiniband\'"' },
      ],
    },
    {
      label: "Physical check hints",
      cmds: [
        { cmd: "# Symbol errors rising → cable/optic likely" },
        { cmd: "# Link flapping → check clamp, clean fiber" },
        { cmd: "# Port down + neighbor down → switch port issue" },
      ],
    },
    {
      label: "If confirmed hardware",
      cmds: [
        { cmd: 'scontrol update NodeName=<NODE> State=drain Reason="IB port down"', danger: true },
        { cmd: "# Escalate to network team with LID + port" },
      ],
    },
  ],

  IBSymbolErrorSpike: [
    {
      label: "Inspect",
      cmds: [
        { cmd: 'perfquery -a | grep -iE "symbol|link"' },
        { cmd: "ibqueryerrors --data" },
      ],
    },
    {
      label: "Clear + observe",
      cmds: [
        { cmd: "ibqueryerrors --clear-errors=all", note: "wait 5 min, re-check growth rate", danger: true },
      ],
    },
    {
      label: "Likely causes",
      cmds: [
        { cmd: "# Rising steadily → cable/optic degradation" },
        { cmd: "# Spike after maintenance → dirty connector" },
        { cmd: "# Only one direction → directional link issue" },
      ],
    },
    {
      label: "Correlate",
      cmds: [
        { cmd: "sacct -S now-1hour --format=JobID,NodeList,State | grep <NODE>" },
      ],
    },
  ],

  // Alerts that fall back to a minimal, generic set
  HighGPUTemperature: [
    {
      label: "Inspect",
      cmds: [
        { cmd: 'ssh <NODE> "nvidia-smi -q -d TEMPERATURE,POWER -i <GPU_IDX>"' },
        { cmd: 'ssh <NODE> "dmesg -T | grep -iE \'thermal|throttl\'"' },
      ],
    },
    {
      label: "Mitigate",
      cmds: [
        { cmd: 'ssh <NODE> "sudo nvidia-smi -pl <WATTS> -i <GPU_IDX>"', note: "cap power short-term", danger: true },
      ],
    },
  ],

  GPUMemoryHigh: [
    {
      label: "Inspect",
      cmds: [
        { cmd: 'ssh <NODE> "nvidia-smi --query-gpu=memory.used,memory.free,memory.total --format=csv -i <GPU_IDX>"' },
        { cmd: 'ssh <NODE> "nvidia-smi --query-gpu=memory.used --format=csv -l 5 -i <GPU_IDX>"' },
      ],
    },
    {
      label: "Workload",
      cmds: [
        { cmd: "scontrol show job <JOB_ID>" },
        { cmd: "# Try: activation checkpointing, ZeRO-3, smaller batch, gradient accumulation" },
      ],
    },
  ],

  NodeDown: [
    {
      label: "Verify",
      cmds: [
        { cmd: "ping -c 3 <NODE>" },
        { cmd: "scontrol show node <NODE>" },
        { cmd: "sinfo -N -l | grep <NODE>" },
      ],
    },
    {
      label: "Out-of-band",
      cmds: [
        { cmd: "ipmitool -H <BMC_IP> power status" },
        { cmd: "ipmitool -H <BMC_IP> sel list | tail -20" },
      ],
    },
    {
      label: "Recover",
      cmds: [
        { cmd: "scontrol update NodeName=<NODE> State=resume", danger: true },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

export function resolveCheatsheet(ctx: CheatsheetCtx): Section[] {
  if (ctx.card === "alert") {
    const name = ctx.data?.alert_name;
    if (name && ALERT_CONTENT[name]) return ALERT_CONTENT[name];
    // Unknown alert → generic troubleshooting pointers
    return [
      {
        label: "Inspect",
        cmds: [
          { cmd: "scontrol show node <NODE>" },
          { cmd: 'ssh <NODE> "dmesg -T | tail -50"' },
          { cmd: 'ssh <NODE> "journalctl -k -S \'30 min ago\'"' },
        ],
      },
    ];
  }
  return CARD_CONTENT[ctx.card] ?? [];
}
